/**
 * Hook to manage household onboarding state.
 * Detects if user needs onboarding (no household) and persists completion.
 */

import { useState, useCallback, useEffect } from 'react';
import { getMyHousehold } from '../api/household';
import { ApiClientError } from '../api/boxes';

const STORAGE_KEY = 'peekbox_household_onboarding_complete';
const SKIPPED_KEY = 'peekbox_household_onboarding_skipped';

export interface UseHouseholdOnboardingReturn {
  /** Whether the household onboarding wizard should be shown */
  shouldShow: boolean;
  /** Whether onboarding is currently being checked */
  checking: boolean;
  /** Mark onboarding as complete (hides wizard, persists to localStorage) */
  complete: () => void;
  /** Mark onboarding as skipped (creates minimal household, allows resume later) */
  skip: () => void;
}

export function useHouseholdOnboarding(): UseHouseholdOnboardingReturn {
  const [shouldShow, setShouldShow] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    async function checkHousehold() {
      // Check localStorage first
      try {
        const isComplete = localStorage.getItem(STORAGE_KEY) === 'true';
        if (isComplete) {
          setShouldShow(false);
          setChecking(false);
          return;
        }
      } catch {
        // Storage unavailable, continue with API check
      }

      // Check if user has a household
      try {
        await getMyHousehold();
        // User has a household, no onboarding needed
        setShouldShow(false);
      } catch (err) {
        if (err instanceof ApiClientError && err.statusCode === 404) {
          // No household, show onboarding
          setShouldShow(true);
        } else {
          // Other error, don't show onboarding
          setShouldShow(false);
        }
      } finally {
        setChecking(false);
      }
    }

    checkHousehold();
  }, []);

  const complete = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
      localStorage.removeItem(SKIPPED_KEY);
    } catch {
      // Storage unavailable; hide for this session only
    }
    setShouldShow(false);
  }, []);

  const skip = useCallback(() => {
    try {
      localStorage.setItem(SKIPPED_KEY, 'true');
    } catch {
      // Storage unavailable; hide for this session only
    }
    setShouldShow(false);
  }, []);

  return {
    shouldShow,
    checking,
    complete,
    skip,
  };
}
